import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { HomeDevice, NetworkDiagnosticsReport } from '../types';
import {
  fetchDevices,
  addHomeDevice,
  removeHomeDevice,
  fetchNetworkDiagnostics,
  healHouseholdService,
  sendFissalChat
} from '../services/api';
import { triggerHaptic } from '../services/purrSynthesizer';
import {
  Smartphone,
  Tablet,
  Tv,
  Laptop,
  Camera,
  Plug,
  Plus,
  Trash2,
  RefreshCw,
  ShieldCheck,
  Wifi,
  X,
  Activity,
  Wrench,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Globe,
  Server,
  Send
} from 'lucide-react';

export const FamilyDeviceManagerView: React.FC = () => {
  const { profile: activeProfile, refreshStatus } = useApp();
  const [devices, setDevices] = useState<HomeDevice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Deep Network Diagnostics & Self-Heal State
  const [diagReport, setDiagReport] = useState<NetworkDiagnosticsReport | null>(null);
  const [diagLoading, setDiagLoading] = useState<boolean>(false);
  const [healingTarget, setHealingTarget] = useState<string | null>(null);
  const [healResult, setHealResult] = useState<string | null>(null);
  const [fissalPrompt, setFissalPrompt] = useState<string>('');
  const [fissalWorking, setFissalWorking] = useState<boolean>(false);

  // Add Device Form fields
  const [name, setName] = useState<string>('');
  const [ip, setIp] = useState<string>('');
  const [type, setType] = useState<HomeDevice['type']>('phone');
  const [formError, setFormError] = useState<string | null>(null);

  const loadAll = async () => {
    setLoading(true);
    setDiagLoading(true);
    const [devRes, netRes] = await Promise.all([
      fetchDevices(),
      fetchNetworkDiagnostics()
    ]);
    if (devRes.ok) {
      setDevices(devRes.devices);
    }
    if (netRes) {
      setDiagReport(netRes);
    }
    setLoading(false);
    setDiagLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleHeal = async (
    service: 'all' | 'cameras' | 'photos' | 'network' | 'dns' | 'dashboard' | 'bot',
    label: string
  ) => {
    if (healingTarget) return;
    triggerHaptic([30, 50, 30]);
    setHealingTarget(service);
    setHealResult(`🛠️ Fissal is diagnosing and repairing ${label}...`);
    try {
      const res = await healHouseholdService(service);
      if (res.ok) {
        setHealResult(`✅ ${res.message || `${label} repaired and verified online!`}`);
      } else {
        setHealResult(`⚠️ Repair notice: ${res.error || 'Could not complete self-heal.'}`);
      }
      await loadAll();
      refreshStatus();
    } catch (e: any) {
      setHealResult(`⚠️ Self-heal error: ${e.message}`);
    } finally {
      setHealingTarget(null);
    }
  };

  const handleAskFissalToFix = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fissalPrompt.trim() || fissalWorking) return;
    triggerHaptic(25);
    setFissalWorking(true);
    setHealResult('🐾 Fissal is running network diagnostics and repairing the issue...');
    try {
      const prompt = `Family member needs help fixing an issue while Echo is away: "${fissalPrompt.trim()}". Please run diagnostics and execute any necessary self-healing tools to fix it.`;
      const res = await sendFissalChat(prompt, [], activeProfile);
      setHealResult(res.answer || 'Fissal completed the diagnostic and repair pass.');
      setFissalPrompt('');
      await loadAll();
    } catch (err: any) {
      setHealResult(`⚠️ Could not reach Fissal AI: ${err.message}. Try tapping "Fix Everything" above.`);
    } finally {
      setFissalWorking(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !ip.trim()) {
      setFormError('Please provide both a name and an IP address.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    const res = await addHomeDevice(name.trim(), ip.trim(), type);
    if (res.ok) {
      triggerHaptic(30);
      setName('');
      setIp('');
      setType('phone');
      setModalOpen(false);
      const devRes = await fetchDevices();
      if (devRes.ok) setDevices(devRes.devices);
    } else {
      setFormError(res.error || 'Failed to add device.');
    }
    setSubmitting(false);
  };

  const handleRemove = async (id: string, devName: string) => {
    if (!window.confirm(`Remove ${devName} from recognized devices?`)) return;
    triggerHaptic(20);
    const res = await removeHomeDevice(id);
    if (res.ok) {
      const devRes = await fetchDevices();
      if (devRes.ok) setDevices(devRes.devices);
    }
  };

  const getDeviceIcon = (t: HomeDevice['type']) => {
    switch (t) {
      case 'phone':
        return <Smartphone className="w-5 h-5 text-sky-400" />;
      case 'tablet':
        return <Tablet className="w-5 h-5 text-indigo-400" />;
      case 'tv':
        return <Tv className="w-5 h-5 text-amber-400" />;
      case 'laptop':
        return <Laptop className="w-5 h-5 text-emerald-400" />;
      case 'camera':
        return <Camera className="w-5 h-5 text-rose-400" />;
      default:
        return <Plug className="w-5 h-5 text-slate-400" />;
    }
  };

  const checks = diagReport?.checks;

  return (
    <div className="flex-1 flex flex-col h-full overflow-y-auto p-3.5 space-y-4 pb-28 bg-[#070a0f]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white tracking-tight flex items-center gap-2 font-cinzel">
            <Activity className="w-5 h-5 text-emerald-400" />
            <span>Network Diagnostics & Self-Heal</span>
          </h2>
          <p className="text-xs text-slate-400">
            Check internet, cameras, & server health — or command Fissal to fix issues if Echo is away.
          </p>
        </div>
        <button
          onClick={() => {
            triggerHaptic(15);
            loadAll();
          }}
          disabled={diagLoading}
          className="px-3 py-2 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 text-xs font-mono font-bold flex items-center gap-1.5 transition-all disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${diagLoading ? 'animate-spin' : ''}`} />
          <span>{diagLoading ? 'Checking...' : 'Run Check'}</span>
        </button>
      </div>

      {/* 1. LIVE NETWORK & HOMELAB HEALTH MATRIX */}
      <div className="p-3.5 rounded-2xl bg-slate-900/90 border border-white/10 shadow-xl space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-white flex items-center gap-1.5 font-cinzel">
            <Globe className="w-4 h-4 text-sky-400" />
            <span>Live Household Network Checks</span>
          </span>
          {diagReport?.system && (
            <span className="text-[10px] font-mono text-slate-400">
              CPU {diagReport.system.cpuPercent}% · RAM {diagReport.system.memPercent}% · Disk {diagReport.system.diskPercent}%
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {/* Internet Uplink */}
          <div className="p-2.5 rounded-xl bg-black/50 border border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono text-slate-400">Internet (WAN)</div>
              <div className="text-xs font-bold text-white mt-0.5">
                {checks?.internet?.ok !== false ? 'Connected' : 'Offline'}
              </div>
              <div className="text-[10px] font-mono text-emerald-400">
                {checks?.internet?.latencyMs ? `${checks.internet.latencyMs} ms` : '1.1.1.1 OK'}
              </div>
            </div>
            <CheckCircle2 className={`w-4 h-4 ${checks?.internet?.ok !== false ? 'text-emerald-400' : 'text-rose-400'}`} />
          </div>

          {/* LAN Gateway Router */}
          <div className="p-2.5 rounded-xl bg-black/50 border border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono text-slate-400">Wi-Fi Router</div>
              <div className="text-xs font-bold text-white mt-0.5">
                {checks?.lanGateway?.ok !== false ? 'Online' : 'Check Router'}
              </div>
              <div className="text-[10px] font-mono text-sky-400">
                {checks?.lanGateway?.latencyMs ? `${checks.lanGateway.latencyMs} ms` : '192.168.68.1'}
              </div>
            </div>
            <CheckCircle2 className={`w-4 h-4 ${checks?.lanGateway?.ok !== false ? 'text-emerald-400' : 'text-rose-400'}`} />
          </div>

          {/* Frigate Cameras NVR */}
          <div className="p-2.5 rounded-xl bg-black/50 border border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono text-slate-400">Cameras (NVR)</div>
              <div className="text-xs font-bold text-white mt-0.5">
                {checks?.frigate?.ok !== false ? 'Recording' : 'Needs Restart'}
              </div>
              <div className="text-[10px] font-mono text-amber-300">
                {checks?.frigate?.version ? `v${checks.frigate.version}` : 'Port 5000'}
              </div>
            </div>
            {checks?.frigate?.ok !== false ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-amber-400" />
            )}
          </div>

          {/* Immich Family Photos */}
          <div className="p-2.5 rounded-xl bg-black/50 border border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono text-slate-400">Photo Vault</div>
              <div className="text-xs font-bold text-white mt-0.5">
                {checks?.immich?.ok !== false ? 'Ready' : 'Standby'}
              </div>
              <div className="text-[10px] font-mono text-sky-300">Immich :2283</div>
            </div>
            <CheckCircle2 className={`w-4 h-4 ${checks?.immich?.ok !== false ? 'text-emerald-400' : 'text-amber-400'}`} />
          </div>

          {/* Pi-hole DNS Shield */}
          <div className="p-2.5 rounded-xl bg-black/50 border border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono text-slate-400">DNS & Ad-Shield</div>
              <div className="text-xs font-bold text-white mt-0.5">
                {checks?.piholeDns?.ok !== false ? 'Protected' : 'Bypassed'}
              </div>
              <div className="text-[10px] font-mono text-emerald-300">Pi-hole :53</div>
            </div>
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
          </div>

          {/* Tailscale Mesh */}
          <div className="p-2.5 rounded-xl bg-black/50 border border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono text-slate-400">Remote Relay</div>
              <div className="text-xs font-bold text-white mt-0.5">
                {checks?.tailscale?.ok !== false ? 'Mesh Active' : 'LAN Only'}
              </div>
              <div className="text-[10px] font-mono text-purple-300">
                {checks?.tailscale?.ip || '100.83.56.28'}
              </div>
            </div>
            <Server className="w-4 h-4 text-purple-400" />
          </div>
        </div>

        {/* PM2 Services Status Strip */}
        {diagReport?.services && diagReport.services.length > 0 && (
          <div className="pt-1">
            <div className="text-[10px] font-mono text-slate-400 uppercase mb-1.5">
              Core Server Daemons ({diagReport.services.filter(s => s.status === 'online').length}/{diagReport.services.length} Online)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {diagReport.services.map((svc) => (
                <span
                  key={svc.id}
                  className={`px-2 py-1 rounded-lg text-[10px] font-mono border flex items-center gap-1.5 ${
                    svc.status === 'online'
                      ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'
                      : 'bg-rose-500/15 border-rose-500/30 text-rose-300'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${svc.status === 'online' ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                  <span>{svc.name}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 2. ONE-TAP AUTONOMOUS SELF-HEALING CONSOLE */}
      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-amber-500/15 via-slate-900/90 to-black border border-amber-500/30 shadow-xl space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xs font-bold text-amber-300 flex items-center gap-1.5 font-cinzel">
              <Wrench className="w-4 h-4 text-amber-400" />
              <span>1-Tap Self-Healing (Command Fissal to Fix Issues)</span>
            </h3>
            <p className="text-[11px] text-slate-300 mt-0.5">
              If Echo is away and cameras, photos, or Wi-Fi act up, tap a button below and Fissal will fix it automatically.
            </p>
          </div>
        </div>

        {/* Self-Healing Buttons Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <button
            onClick={() => handleHeal('all', 'All Household Systems')}
            disabled={Boolean(healingTarget)}
            className="col-span-2 sm:col-span-1 p-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 font-bold text-xs flex items-center justify-center gap-1.5 shadow-lg transition-all active:scale-95"
          >
            <Sparkles className="w-4 h-4" />
            <span>{healingTarget === 'all' ? 'Healing All...' : 'Fix Everything'}</span>
          </button>

          <button
            onClick={() => handleHeal('cameras', 'Perimeter Cameras & NVR')}
            disabled={Boolean(healingTarget)}
            className="p-2.5 rounded-xl bg-white/5 hover:bg-amber-500/20 border border-white/10 hover:border-amber-400/40 text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all active:scale-95"
          >
            <Camera className="w-3.5 h-3.5 text-amber-400" />
            <span>Fix Cameras</span>
          </button>

          <button
            onClick={() => handleHeal('photos', 'Immich Photo Vault')}
            disabled={Boolean(healingTarget)}
            className="p-2.5 rounded-xl bg-white/5 hover:bg-sky-500/20 border border-white/10 hover:border-sky-400/40 text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all active:scale-95"
          >
            <span>🖼️ Fix Photos</span>
          </button>

          <button
            onClick={() => handleHeal('network', 'Home DNS & Network Relay')}
            disabled={Boolean(healingTarget)}
            className="p-2.5 rounded-xl bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-400/40 text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all active:scale-95"
          >
            <Wifi className="w-3.5 h-3.5 text-emerald-400" />
            <span>Fix Wi-Fi / DNS</span>
          </button>

          <button
            onClick={() => handleHeal('dashboard', 'Homelab Dashboard & Bot')}
            disabled={Boolean(healingTarget)}
            className="p-2.5 rounded-xl bg-white/5 hover:bg-purple-500/20 border border-white/10 hover:border-purple-400/40 text-slate-200 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all active:scale-95"
          >
            <Server className="w-3.5 h-3.5 text-purple-400" />
            <span>Fix Server Apps</span>
          </button>
        </div>

        {/* Describe Any Problem to Fissal */}
        <form onSubmit={handleAskFissalToFix} className="flex items-center gap-2 pt-1">
          <input
            type="text"
            value={fissalPrompt}
            onChange={(e) => setFissalPrompt(e.target.value)}
            placeholder="Or describe what is wrong (e.g. 'Porch camera is frozen')..."
            className="flex-1 bg-black/60 border border-white/15 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400"
          />
          <button
            type="submit"
            disabled={!fissalPrompt.trim() || fissalWorking}
            className="px-3.5 py-2 rounded-xl bg-amber-500/25 hover:bg-amber-500/35 border border-amber-400/50 text-amber-200 font-bold text-xs flex items-center gap-1.5 disabled:opacity-40 transition-all"
          >
            <Send className="w-3.5 h-3.5" />
            <span>Command Fissal</span>
          </button>
        </form>

        {/* Self-Healing Output Box */}
        {healResult && (
          <div className="p-3 rounded-xl bg-black/70 border border-amber-500/40 text-xs text-amber-100 leading-relaxed whitespace-pre-wrap animate-in fade-in">
            <div className="flex items-center justify-between mb-1">
              <span className="font-bold text-amber-400">🐾 Fissal Repair Report:</span>
              <button onClick={() => setHealResult(null)} className="text-[10px] text-slate-400 hover:text-white">
                Dismiss
              </button>
            </div>
            {healResult}
          </div>
        )}
      </div>

      {/* 3. RECOGNIZED HOUSEHOLD WI-FI DEVICES */}
      <div className="flex items-center justify-between pt-2">
        <div>
          <h3 className="text-sm font-bold text-white flex items-center gap-2 font-cinzel">
            <Wifi className="w-4 h-4 text-emerald-400" />
            <span>Recognized Family Wi-Fi Devices ({devices.length})</span>
          </h3>
        </div>
        <button
          onClick={() => {
            triggerHaptic(25);
            setModalOpen(true);
          }}
          className="px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs flex items-center gap-1 shadow-md transition-all active:scale-95"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add Device</span>
        </button>
      </div>

      {loading && devices.length === 0 ? (
        <div className="p-8 text-center text-slate-500 text-xs font-mono">
          Scanning household relay...
        </div>
      ) : devices.length === 0 ? (
        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 text-center space-y-2">
          <p className="text-xs text-slate-400">
            Add family phones, tablets, or TVs so Fissal recognizes them on the home network.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {devices.map((dev) => (
            <div
              key={dev.id}
              className="p-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-all flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  {getDeviceIcon(dev.type)}
                </div>
                <div>
                  <h4 className="text-xs font-bold text-white leading-tight">{dev.name}</h4>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-mono text-[10px] text-slate-400">{dev.ip}</span>
                    <span className="text-[9px] uppercase font-bold px-1.5 py-0.2 rounded bg-white/5 text-slate-400 border border-white/5">
                      {dev.type}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleRemove(dev.id, dev.name)}
                className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                title="Forget device"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Device Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
          <div className="w-full max-w-sm rounded-2xl bg-[#0d121a] border border-white/15 p-5 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Plus className="w-4 h-4 text-emerald-400" />
                <span>Register New Household Device</span>
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {formError && (
              <div className="p-2.5 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs">
                {formError}
              </div>
            )}

            <form onSubmit={handleAdd} className="space-y-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Device Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Lycan's Phone, Living Room TV"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 text-xs focus:outline-none focus:border-emerald-400"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  IP Address
                </label>
                <input
                  type="text"
                  placeholder="e.g. 192.168.68.85"
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 text-xs font-mono focus:outline-none focus:border-emerald-400"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Device Type
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as HomeDevice['type'])}
                  className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-emerald-400"
                >
                  <option value="phone">📱 Smartphone</option>
                  <option value="tablet">📟 Tablet</option>
                  <option value="tv">📺 Smart TV</option>
                  <option value="laptop">💻 Laptop / PC</option>
                  <option value="camera">📹 Security Camera</option>
                  <option value="other">🔌 Smart Plug / Other</option>
                </select>
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-3 py-2 rounded-xl text-xs text-slate-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs shadow-md shadow-emerald-500/20 disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Add Device'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
